import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import format from 'date-fns/format';
import parse from 'date-fns/parse';
import startOfWeek from 'date-fns/startOfWeek';
import getDay from 'date-fns/getDay';
import { post } from '../../Auths/AuthToken';
import {
  compareAsc,
  endOfMonth,
  endOfWeek,
  isWithinInterval,
  parseISO,
  startOfDay,
  startOfMonth,
} from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { setAppPopupFacility } from '../../../redux/slices/scheduler/appointmentSlice';

function AppCalander({
  providerId = 0,
  appDate,
  callback = () => {},
  facilityList = [],
  facilityLoc,
  mode = 0,
  setMode = () => {},
}) {
  const [allData, setAllData] = useState([]);
  const [allBlockData, setAllBlockData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [facilitySetting, setFacilitySetting] = useState(null);
  const [calTime, setCalTime] = useState({
    startTime: null,
    endTime: null,
    scrliiTime: null,
  });

  const dispatch = useDispatch();
  const { user } = useSelector((store) => store.auth);
  const { defaultTimeFormat } = useSelector((store) => store.app);
  const { appPopUpFacility, patSrchDiasbled } = useSelector(
    (store) => store.appointment,
  );

  const [calPeriodFilter, setCalPeriodFilter] = useState({
    name: 'day',
    selectDate: appDate,
    startDate: appDate,
    endDate: appDate,
  });

  const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek: () =>
      startOfWeek(new Date(), {
        weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
      }), // Monday
    getDay,
    locales: {
      'en-US': require('date-fns/locale/en-US'),
    },
  });

  useEffect(() => {
    (async () => {
      if (providerId) {
        setIsLoading(true);
        const response = await post('api/auth/appCalander', {
          resourceId: providerId,
          locationId: mode ? appPopUpFacility : facilityLoc,
          start: format(calPeriodFilter.startDate, 'yyyy-MM-dd'),
          end: format(calPeriodFilter.endDate, 'yyyy-MM-dd'),
          staffUserId: user?.id,
          mode: mode,
        });
        if (response.status === 200) {
          setIsLoading(false);
          setAllData(response.data);
          setAllBlockData(response?.data?.eventsClose || []);
          setFacilitySetting(response?.data?.facilitySetting || []);
          setCalTime({
            startTime: response?.data?.facilitySetting?.facility_first_hour,
            endTime: response?.data?.facilitySetting?.facility_last_hour,
            scrliiTime: response?.data?.facilitySetting?.facility_scroll_hour,
          });
        }
      }
    })();
  }, [
    providerId,
    appPopUpFacility,
    calPeriodFilter,
    user,
    appDate,
    facilityLoc,
    mode,
  ]);

  const onRangeChange = useCallback(
    (range, view) => {
      if (view && calPeriodFilter?.name !== view) {
        if (range.length === 1 && view === 'day') {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'day',
            startDate: calPeriodFilter.selectDate,
            endDate: calPeriodFilter.selectDate,
          });
        } else if (range.length === 7) {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'week',
            startDate: startOfWeek(calPeriodFilter.selectDate, {
              weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
            }),
            endDate: endOfWeek(calPeriodFilter.selectDate, {
              weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
            }),
          });
        } else {
          setCalPeriodFilter({
            ...calPeriodFilter,
            name: 'month',
            startDate: startOfMonth(calPeriodFilter.selectDate),
            endDate: endOfMonth(calPeriodFilter.selectDate),
          });
        }
      }
    },
    [calPeriodFilter, facilitySetting?.facility_start_day],
  );

  const onNavigate = (newDate, view, action) => {
    if (view === 'day') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'day',
        selectDate: newDate,
        startDate: newDate,
        endDate: newDate,
      });
      callback(new Date(newDate));
    } else if (view === 'week') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'week',
        selectDate: newDate,
        startDate: startOfWeek(newDate, {
          weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
        }),
        endDate: endOfWeek(newDate, {
          weekStartsOn: facilitySetting?.facility_start_day === '1' ? 1 : 0,
        }),
      });
    } else if (view === 'month') {
      setCalPeriodFilter({
        ...calPeriodFilter,
        name: 'month',
        selectDate: newDate,
        startDate: startOfMonth(newDate),
        endDate: endOfMonth(newDate),
      });
    }
  };

  const eventPropGetter = useCallback((event) => {
    let bgColor = 'blue';
    if (event.app_status === '1') {
      bgColor = 'gray';
    } else if (event.app_status === '2') {
      bgColor = 'gray';
    } else if (event.app_status === '3') {
      bgColor = 'gray';
    } else {
      if (event.app_color) {
        bgColor = event.app_color;
      }
    }

    return {
      style: {
        color: 'black',
        backgroundColor: bgColor,
        pointerEvents: 'auto',
      },
    };
  }, []);

  const handleSelectSlot = useCallback(
    (event) => {
      if (calPeriodFilter?.name === 'month' || event.start.getHours() >= 7) {
        callback(new Date(event?.start));
      }
    },
    [callback, calPeriodFilter],
  );

  const onSelectEvent = useCallback(
    (event) => {
      callback(new Date(event?.start));
    },
    [callback],
  );

  const dayPropGetter = useCallback(
    (date) => {
      let overrideBlock =
        allData?.overrideSetting && allData?.overrideSetting?.schedule_type;
      let overrideBlockDateStart =
        allData?.overrideSetting && allData?.overrideSetting?.start_date;
      let overrideBlockDateEnd =
        allData?.overrideSetting && allData?.overrideSetting?.end_date;
      if (
        overrideBlock === '1' &&
        overrideBlockDateStart &&
        overrideBlockDateEnd
      ) {
        const startDate = new Date(overrideBlockDateStart);
        const endDate = new Date(overrideBlockDateEnd);
        if (
          startOfDay(date) >= startOfDay(startDate) &&
          startOfDay(date) <= startOfDay(endDate)
        ) {
          return null;
        }
      }

      const providerScheduleEnd =
        allData?.providerScheduleEnd &&
        new Date(allData?.providerScheduleEnd?.end_date);
      if (
        providerScheduleEnd &&
        startOfDay(providerScheduleEnd) <= startOfDay(date)
      ) {
        return {
          className: 'blocked-day',
          style: {
            pointerEvents: 'none ',
            backgroundColor: '#7a7e7c',
          },
        };
      }

      const dayOfWeek = date.getDay() === 0 ? 7 : date.getDay();
      const isBlocked =
        allBlockData &&
        allBlockData.some((item) => {
          return parseInt(item.day_id) === parseInt(dayOfWeek);
        });

      if (allBlockData.length > 0 && isBlocked) {
        return {
          className: 'blocked-day',
          style: {
            pointerEvents: 'none ',
            backgroundColor: '#7a7e7c',
          },
        };
      }
      return {};
    },
    [allBlockData, allData?.providerScheduleEnd, allData?.overrideSetting],
  );

  const { formats } = useMemo(
    () => ({
      formats: {
        eventTimeRangeFormat: ({ start, end }, culture, localizer) => {},
      },
    }),
    [],
  );

  const slotPropGetter = useCallback(
    (date) => {
      const hour = date.getHours();
      const minute = date.getMinutes();

      const fbsh = new Date(allData?.firstBreakStart).getHours();
      const fbsm = new Date(allData?.firstBreakStart).getMinutes();

      const fbeh = new Date(allData?.firstBreakEnd).getHours();
      const fbem = new Date(allData?.firstBreakEnd).getMinutes();

      const sbsh = new Date(allData?.secondBreakStart).getHours();
      const sbsm = new Date(allData?.secondBreakStart).getMinutes();

      const sbeh = new Date(allData?.secondBreakEnd).getHours();
      const sbem = new Date(allData?.secondBreakEnd).getMinutes();

      const isAvailableInIntervals =
        allData?.openMyCalander?.some((interval) => {
          const start = parseISO(interval.start_time);
          const end = parseISO(interval.end_time);

          // Ensure a valid interval (start must be before end)
          if (compareAsc(start, end) >= 0) return false;

          return isWithinInterval(date, { start, end });
        }) || false;

      return {
        ...(allData?.startHour &&
          calPeriodFilter.name === 'day' &&
          (date.getHours() < new Date(allData?.startHour).getHours() ||
            (date.getHours() === new Date(allData?.startHour).getHours() &&
              date.getMinutes() <
                new Date(allData?.startHour).getMinutes())) && {
            style: {
              backgroundColor: 'gray',
              color: 'white',
            },
          }),
        ...(allData?.lastHour &&
          calPeriodFilter.name === 'day' &&
          (date.getHours() > new Date(allData?.lastHour).getHours() ||
            (date.getHours() === new Date(allData?.lastHour).getHours() &&
              date.getMinutes() >=
                new Date(allData?.lastHour).getMinutes())) && {
            style: {
              backgroundColor: 'gray',
              color: 'white',
            },
          }),
        ...(calPeriodFilter.name === 'day' &&
          (hour > fbsh || (hour === fbsh && minute >= fbsm)) &&
          (hour < fbeh || (hour === fbeh && minute < fbem)) && {
            style: {
              backgroundColor: 'powderblue',
              color: 'black',
            },
          }),
        ...(calPeriodFilter.name === 'day' &&
          (hour > sbsh || (hour === sbsh && minute >= sbsm)) &&
          (hour < sbeh || (hour === sbeh && minute < sbem)) && {
            style: {
              backgroundColor: 'powderblue',
              color: 'black',
            },
          }),
        ...(isAvailableInIntervals && {
          style: {
            // These styles will be applied if isAvailable is false
            backgroundColor: '#fff', // Added a default color here for clarity
            color: 'black',
          },
        }),
      };
    },
    [
      allData?.startHour,
      allData?.lastHour,
      allData?.firstBreakStart,
      allData?.firstBreakEnd,
      calPeriodFilter.name,
      allData?.secondBreakEnd,
      allData?.secondBreakStart,
      allData?.openMyCalander,
    ],
  );

  const handleCheckboxChange = (event) => {
    //const { name, value, checked } = event.target;
    dispatch(setAppPopupFacility(parseInt(event.target.value)));

    setMode(1);
  };

  const CustomToolbar = (toolbar) => {
    return (
      <div
        className="rbc-toolbar"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '10px',
          fontSize: '14px',
        }}
      >
        <div style={{ display: 'flex', width: '100%', margin: '10px' }}>
          {(calPeriodFilter.name === 'day' && allData?.displayDate && (
            <span className="rbc-toolbar-labels">{`${allData?.dayName}, ${allData?.displayDate}:  ${allData?.providerOfficeTime}`}</span>
          )) ||
            (calPeriodFilter.name === 'week' && (
              <span className="rbc-toolbar-labels">{`Week: ${format(
                calPeriodFilter?.startDate,
                'MMMM d, yyyy',
              )} to ${format(calPeriodFilter?.endDate, 'MMMM d, yyyy')}`}</span>
            )) ||
            (calPeriodFilter.name === 'month' && (
              <span className="rbc-toolbar-labels">{`Month: ${format(
                calPeriodFilter?.endDate,
                'MMMM, yyyy',
              )}`}</span>
            ))}
        </div>
        <div
          style={{
            display: 'flex',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <span className="rbc-btn-group">
            <button
              type="button"
              onClick={
                patSrchDiasbled ? () => {} : () => toolbar.onNavigate('PREV')
              }
            >
              Prev
            </button>
            <button
              type="button"
              onClick={
                patSrchDiasbled ? () => {} : () => toolbar.onNavigate('TODAY')
              }
            >
              Today
            </button>
            <button
              type="button"
              onClick={
                patSrchDiasbled ? () => {} : () => toolbar.onNavigate('NEXT')
              }
            >
              Next
            </button>
          </span>
          {/* {() => {
          if (calPeriodFilter.name === 'day') {
            return <span className="rbc-toolbar-label">{toolbar.label}</span>;
          }
        }} */}
          {/* <span className="rbc-toolbar-label">{toolbar.label}</span> */}
          <span className="rbc-btn-group">
            <button
              className={calPeriodFilter.name === 'day' ? 'rbc-active' : ''}
              type="button"
              onClick={() => toolbar.onView('day')}
            >
              Day
            </button>
            <button
              className={calPeriodFilter.name === 'week' ? 'rbc-active' : ''}
              type="button"
              onClick={() => toolbar.onView('week')}
            >
              Week
            </button>
            <button
              className={calPeriodFilter.name === 'month' ? 'rbc-active' : ''}
              type="button"
              onClick={() => toolbar.onView('month')}
            >
              Month
            </button>
          </span>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className=" width100 apt-sldr-loc-sec">
        <div style={{ display: 'flex' }}>
          {/*allData?.locList &&
            allData?.locList.map((item, i) => (
              <div
                key={item.id}
                className="sldr-loctn-slectn"
                style={{ width: '30%' }}
              >
                <span className="">
                  <input
                    type="checkbox"
                    name={item?.location_code}
                    checked={checkboxes[item?.location_code]}
                    onChange={handleCheckboxChange}
                    value={item?.id}
                  />
                </span>
                <span className="" style={{ padding: '0px 10px' }}>
                  {item?.location_code}{' '}
                  {`${item?.is_telemedicine === '1' ? '(Remote)' : ''}`}
                </span>
              </div>
            ))*/}
          <div className="sldr-loctn-slectn" style={{ width: '30%' }}>
            <span className="">
              <input
                type="radio"
                name={'appPopUpFacility'}
                checked={
                  appPopUpFacility === parseInt(allData?.defaultLocation)
                }
                onChange={handleCheckboxChange}
                value={
                  allData?.defaultLocation
                    ? parseInt(allData.defaultLocation)
                    : 0
                }
              />
            </span>
            {!isLoading ? (
              <span className="" style={{ padding: '0px 10px' }}>
                {allData?.defaultLocationName}
                {`${allData?.is_telemedicine === '1' ? '(Remote)' : ''}`}
              </span>
            ) : (
              <span className="" style={{ padding: '0px 10px' }}>
                Loading...
              </span>
            )}
          </div>
          <div className="sldr-loctn-slectn" style={{ width: '30%' }}>
            <span className="">
              <input
                type="radio"
                name={'appPopUpFacility'}
                checked={appPopUpFacility === 0}
                onChange={handleCheckboxChange}
                value={0}
              />
            </span>
            <span className="" style={{ padding: '0px 10px' }}>
              All Locations
              {`${allData?.is_telemedicine === '1' ? '(Remote)' : ''}`}
            </span>
          </div>
        </div>
        <div className="left med-label width100 fontsize14">
          Total appointments: {allData?.appointmentTotalCount}
          &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;New patients:
          {allData?.appointmentTotalCountNewPt}
        </div>
      </div>

      {/* {isLoading ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '500px',
            backgroundColor: '#f0f0f0',
            borderRadius: '4px',
            margin: '10px 10px',
            width: '570px',
            fontSize: '16px',
            color: '#666',
          }}
        >
          Loading...
        </div>
      ) : ( */}
      <Calendar
        key={allData?.events?.length}
        localizer={localizer}
        {...(defaultTimeFormat?.withoutSecond === 'HH:mm'
          ? {
              formats: {
                timeGutterFormat: 'HH:mm', // 24hr time format
                eventTimeRangeFormat: ({ start, end }) =>
                  `${start.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // 24hr format
                  })} - ${end.toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                    hour12: false, // 24hr format
                  })}`,
              },
            }
          : formats)}
        events={allData?.events}
        startAccessor={(event) => {
          return new Date(event.start);
        }}
        endAccessor={(event) => {
          return new Date(event.end);
        }}
        titleAccessor={(event) => {
          let title = event.title;
          if (event.app_status === '1') {
            title = 'No Show';
          } else if (event.app_status === '2') {
            title = 'Canceled';
          } else if (event.app_status === '3') {
            title = 'Rescheduled';
          }
          return title;
        }}
        toolbar={true}
        selectable={true}
        views={['month', 'week', 'day']}
        defaultView={calPeriodFilter.name || 'day'}
        date={calPeriodFilter?.selectDate}
        //defaultDate={calPeriodFilter?.selectDate || new Date()}
        eventPropGetter={eventPropGetter}
        dayPropGetter={dayPropGetter}
        slotPropGetter={slotPropGetter}
        style={{ height: 500, margin: '10px 10px', width: '570px' }}
        min={calTime?.startTime ? new Date(calTime?.startTime) : undefined} // Set the minimum time
        max={calTime?.endTime ? new Date(calTime?.endTime) : undefined} // Set the maximum time
        step={facilitySetting?.calander_default_slot || 15}
        // formats={formats}
        onRangeChange={onRangeChange}
        onNavigate={onNavigate}
        onSelectSlot={handleSelectSlot}
        onSelectEvent={onSelectEvent}
        scrollToTime={
          calTime?.scrliiTime ? new Date(calTime?.scrliiTime) : new Date()
        }
        components={{
          toolbar: CustomToolbar,
        }}
      />
      {/* )} */}
    </div>
  );
}

export default AppCalander;
